import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { SerializedObject, UnpackResponse } from "../../../../api/src/lib";

// The custom field filter of the five household tables: the match of a row
// (R30), the summary of the "Custom fields" item (R18), the removal of saved
// fields and options that the definitions do not list (R33), and the ids that
// the filter hides (R37). Spec `doc-custom-field-filtering-01a11cc6e668eb59`,
// section 5.5. The terms "usable type", "usable definition", "parse",
// "text-type" and "set field" are the terms of that spec.

/**
 * One item of `GET /v1/households/custom-fields`, as the route serialises it
 * (`HouseholdsController.getHouseholdCustomFields`).
 */
export type HouseholdCustomFieldDefinition = SerializedObject<
  UnpackResponse<HouseholdsController["getHouseholdCustomFields"]>
>["data"][number];

/** The seven usable types of `fieldType`. */
export const USABLE_TYPES = [
  "single_select",
  "multi_select",
  "boolean",
  "text",
  "url",
  "number",
  "date",
] as const;

export type UsableType = (typeof USABLE_TYPES)[number];

export function isUsableType(fieldType: string): fieldType is UsableType {
  return (USABLE_TYPES as readonly string[]).includes(fieldType);
}

/** The usable definitions: the items of a usable type (D11). */
export function usableDefinitions(
  definitions: readonly HouseholdCustomFieldDefinition[],
): HouseholdCustomFieldDefinition[] {
  return definitions.filter((definition) => isUsableType(definition.fieldType));
}

/**
 * The value of one field in the filter. `fieldType` is the type of the
 * definition when the advisor set the value.
 *
 * - select types: the ticked options.
 * - `boolean`: the ticked answers, `true` for "Yes" and `false` for "No".
 * - text-type: the typed text.
 * - `number`: the minimum and the maximum; `null` is an empty box.
 * - `date`: "from" and "to" as `YYYY-MM-DD`; `null` is an empty box.
 */
export type CustomFieldFilterEntry = { fieldKey: string } & (
  | { fieldType: "single_select" | "multi_select"; options: string[] }
  | { fieldType: "boolean"; answers: boolean[] }
  | { fieldType: "text" | "url"; text: string }
  | { fieldType: "number"; min: number | null; max: number | null }
  | { fieldType: "date"; from: string | null; to: string | null }
);

/**
 * The applied custom field filter, in the order in which the advisor set the
 * fields (D41).
 */
export type CustomFieldFilter = CustomFieldFilterEntry[];

/** A row of one of the five tables: `customFields` of the API row. */
export type CustomFieldRow = {
  customFields?: Record<string, unknown> | null;
};

/** True when the entry has a value, so its field is a set field. */
export function isSetField(entry: CustomFieldFilterEntry): boolean {
  switch (entry.fieldType) {
    case "single_select":
    case "multi_select":
      return entry.options.length > 0;
    case "boolean":
      return entry.answers.length > 0;
    case "text":
    case "url":
      return entry.text !== "";
    case "number":
      return entry.min !== null || entry.max !== null;
    case "date":
      return entry.from !== null || entry.to !== null;
  }
}

/**
 * The value of a `number` field, when it parses: a finite number, or a text
 * that `Number()` reads as a finite number. A blank text does not parse,
 * although `Number("")` is 0 (D14).
 */
function parseNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * True when the text is a date in the form `YYYY-MM-DD`. The dates are
 * compared as text, which keeps their order, and never through `new Date()`:
 * `new Date("YYYY-MM-DD")` is midnight UTC, the day before in US time zones.
 */
function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/** A value of a select, `boolean` or text-type field parses when it is a text, a number, `true` or `false`. */
function isScalar(value: unknown): value is string | number | boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

/** True when the value of one field matches the entry (R30). */
function matchesEntry(value: unknown, entry: CustomFieldFilterEntry): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  switch (entry.fieldType) {
    case "single_select":
      // As `multiSelectIncludes` (`filters.tsx`) compares a value.
      return (
        isScalar(value) &&
        entry.options.some((option) => option === String(value))
      );
    case "multi_select":
      // A `multi_select` value parses when it is a list of texts. As
      // `multiSelectIncludes`, one ticked option in the list is a match.
      return (
        Array.isArray(value) &&
        value.every((item) => typeof item === "string") &&
        value.some((item) => entry.options.includes(item))
      );
    case "boolean":
      return (
        (value === true && entry.answers.includes(true)) ||
        (value === false && entry.answers.includes(false))
      );
    case "text":
    case "url":
      return (
        isScalar(value) &&
        String(value).toLowerCase().includes(entry.text.toLowerCase())
      );
    case "number": {
      const parsed = parseNumber(value);
      if (parsed === null) {
        return false;
      }
      // A minimum more than the maximum matches no row (D13).
      return (
        (entry.min === null || parsed >= entry.min) &&
        (entry.max === null || parsed <= entry.max)
      );
    }
    case "date":
      if (!isIsoDate(value)) {
        return false;
      }
      return (
        (entry.from === null || value >= entry.from) &&
        (entry.to === null || value <= entry.to)
      );
  }
}

/**
 * The definition of the entry's field, when it is a usable definition of the
 * type that the entry was set for.
 */
function definitionOf(
  entry: CustomFieldFilterEntry,
  definitions: readonly HouseholdCustomFieldDefinition[],
): HouseholdCustomFieldDefinition | undefined {
  return definitions.find(
    (definition) =>
      definition.fieldKey === entry.fieldKey &&
      definition.fieldType === entry.fieldType,
  );
}

/**
 * True when the row matches each set field (R30). A row with no value for a
 * set field does not match. An entry with no definition in `definitions`, or
 * no value, sets no field: `pruneCustomFieldFilter` removes it (R33).
 */
export function matchesCustomFieldFilter(
  row: CustomFieldRow,
  filter: CustomFieldFilter,
  definitions: readonly HouseholdCustomFieldDefinition[],
): boolean {
  return filter.every((entry) => {
    if (!isSetField(entry) || definitionOf(entry, definitions) === undefined) {
      return true;
    }
    return matchesEntry(row.customFields?.[entry.fieldKey], entry);
  });
}

/** Commas between thousands, decimals unchanged. */
function formatNumber(value: number): string {
  const [whole, decimals] = String(value).split(".");
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimals === undefined ? withCommas : `${withCommas}.${decimals}`;
}

/** `YYYY-MM-DD` as MM/DD/YYYY. */
function formatDate(value: string): string {
  const [year, month, day] = value.split("-");
  return `${month}/${day}/${year}`;
}

/** "from – to", "≥ from" or "≤ to". */
function formatRange(from: string | null, to: string | null): string {
  if (from !== null && to !== null) {
    return `${from} – ${to}`;
  }
  return from !== null ? `≥ ${from}` : `≤ ${to}`;
}

function summarizeValue(
  entry: CustomFieldFilterEntry,
  definition: HouseholdCustomFieldDefinition,
): string {
  switch (entry.fieldType) {
    case "single_select":
    case "multi_select":
      // The ticked options in the order of the definition.
      return (definition.selectOptions ?? [])
        .filter((option) => entry.options.includes(option))
        .join(", ");
    case "boolean":
      // "Yes", then "No" (D34).
      return [true, false]
        .filter((answer) => entry.answers.includes(answer))
        .map((answer) => (answer ? "Yes" : "No"))
        .join(", ");
    case "text":
    case "url":
      return `"${entry.text}"`;
    case "number":
      return formatRange(
        entry.min === null ? null : formatNumber(entry.min),
        entry.max === null ? null : formatNumber(entry.max),
      );
    case "date":
      return formatRange(
        entry.from === null ? null : formatDate(entry.from),
        entry.to === null ? null : formatDate(entry.to),
      );
  }
}

/**
 * The summary of the "Custom fields" item (R18): each set field as
 * "`displayName`: value", in the order of the filter, with " · " between
 * fields. An empty text when no field is set. The item adds ✎ and ✕, or reads
 * "Any ✎", and cuts the summary with "…".
 */
export function summarizeCustomFieldFilter(
  filter: CustomFieldFilter,
  definitions: readonly HouseholdCustomFieldDefinition[],
): string {
  return filter
    .flatMap((entry) => {
      const definition = definitionOf(entry, definitions);
      if (!isSetField(entry) || definition === undefined) {
        return [];
      }
      return [
        `${definition.displayName}: ${summarizeValue(entry, definition)}`,
      ];
    })
    .join(" · ");
}

/**
 * True when the entry has the form of its `fieldType`. The filter is read back
 * from `localStorage`, so an entry can have any form.
 */
function isWellFormed(entry: unknown): entry is CustomFieldFilterEntry {
  if (typeof entry !== "object" || entry === null) {
    return false;
  }
  const value = entry as Record<string, unknown>;
  const isNullOr = (
    item: unknown,
    check: (item: unknown) => boolean,
  ): boolean => item === null || check(item);
  if (typeof value.fieldKey !== "string") {
    return false;
  }
  switch (value.fieldType) {
    case "single_select":
    case "multi_select":
      return (
        Array.isArray(value.options) &&
        value.options.every((option) => typeof option === "string")
      );
    case "boolean":
      return (
        Array.isArray(value.answers) &&
        value.answers.every((answer) => typeof answer === "boolean")
      );
    case "text":
    case "url":
      return typeof value.text === "string";
    case "number":
      return (
        isNullOr(value.min, (item) => typeof item === "number") &&
        isNullOr(value.max, (item) => typeof item === "number")
      );
    case "date":
      return isNullOr(value.from, isIsoDate) && isNullOr(value.to, isIsoDate);
    default:
      return false;
  }
}

/**
 * The filter without each field and each option that the definitions do not
 * list (R33, D12). A field leaves the filter when no usable definition of its
 * type has its key, or when it has no value left. A select option leaves when
 * it is not a select option of the definition. The order of the fields stays.
 */
export function pruneCustomFieldFilter(
  filter: CustomFieldFilter | undefined,
  definitions: readonly HouseholdCustomFieldDefinition[],
): CustomFieldFilter {
  if (!Array.isArray(filter)) {
    return [];
  }
  const usable = usableDefinitions(definitions);
  return filter.flatMap((entry): CustomFieldFilterEntry[] => {
    if (!isWellFormed(entry)) {
      return [];
    }
    const definition = definitionOf(entry, usable);
    if (definition === undefined) {
      return [];
    }
    const pruned: CustomFieldFilterEntry =
      entry.fieldType === "single_select" || entry.fieldType === "multi_select"
        ? {
            ...entry,
            options: entry.options.filter((option) =>
              (definition.selectOptions ?? []).includes(option),
            ),
          }
        : entry;
    return isSetField(pruned) ? [pruned] : [];
  });
}

/**
 * The ids of the rows that the filter hides, for the table to clear from its
 * selection on "Apply (n)" (R37). The filter matches each row of `rows`. The
 * sub rows of a row (`getSubRows`) stay with it, so the sub rows of a hidden
 * row are hidden too: the account rows of a hidden rebalance on
 * Rebalances › Today (R31, `rebalances/lib.tsx`).
 */
export function hiddenRowIds<TRow extends CustomFieldRow>(
  rows: readonly TRow[],
  filter: CustomFieldFilter,
  definitions: readonly HouseholdCustomFieldDefinition[],
  getRowId: (row: TRow) => string,
  getSubRows?: (row: TRow) => readonly TRow[] | undefined,
): string[] {
  const withSubRows = (row: TRow): string[] => [
    getRowId(row),
    ...(getSubRows?.(row) ?? []).flatMap(withSubRows),
  ];
  return rows
    .filter((row) => !matchesCustomFieldFilter(row, filter, definitions))
    .flatMap(withSubRows);
}
